if (!customElements.get('byte-product-description')) {
  class ByteProductDescription extends HTMLElement {
    static #ANIMATION_DURATION_MS = 350;

    constructor() {
      super();

      this.isExpanded = false;
      this.isAnimating = false;
      this.boundHandleClick = this.handleClick.bind(this);
      this.boundHandleResize = this.handleResize.bind(this);
    }

    connectedCallback() {
      this.#cacheElements();
      this.#bindEvents();
      this.#applyTruncation();
    }

    disconnectedCallback() {
      this.#unbindEvents();
    }

    handleClick(event) {
      const actionTrigger = event.target.closest('[data-product-description-action]');
      if (!actionTrigger || this.isAnimating) return;

      const descriptionAction = actionTrigger.dataset.productDescriptionAction;

      if (descriptionAction === 'expand') {
        this.expand();
      }

      if (descriptionAction === 'collapse') {
        this.collapse();
      }
    }

    handleResize() {
      if (this.isExpanded || this.isAnimating) return;

      this.#applyTruncation();
    }

    async expand() {
      if (!this.contentEl || !this.clipEl || this.isExpanded) return;

      const collapsedHeight = this.#getCollapsedHeight();
      const fullHeight = this.contentEl.scrollHeight;

      this.isExpanded = true;
      this.#updateToggleState(true);

      if (this.#prefersReducedMotion() || fullHeight <= collapsedHeight) {
        this.#setExpandedState();
        return;
      }

      await this.#animateHeight(collapsedHeight, fullHeight);
      this.#setExpandedState();
    }

    async collapse() {
      if (!this.contentEl || !this.clipEl || !this.isExpanded) return;

      const collapsedHeight = this.#getCollapsedHeight();
      const fullHeight = this.contentEl.scrollHeight;

      this.isExpanded = false;

      if (this.#prefersReducedMotion()) {
        this.#setCollapsedState(collapsedHeight);
        this.#updateToggleState(false);
        return;
      }

      await this.#animateHeight(fullHeight, collapsedHeight);
      this.#setCollapsedState(collapsedHeight);
      this.#updateToggleState(false);
    }

    #cacheElements() {
      this.clipEl = this.querySelector('[data-product-description-clip]');
      this.contentEl = this.querySelector('[data-product-description-content]');
      this.expandButton = this.querySelector('[data-product-description-action="expand"]');
      this.collapseButton = this.querySelector('[data-product-description-action="collapse"]');
    }

    #bindEvents() {
      this.addEventListener('click', this.boundHandleClick);
      window.addEventListener('resize', this.boundHandleResize);
    }

    #unbindEvents() {
      this.removeEventListener('click', this.boundHandleClick);
      window.removeEventListener('resize', this.boundHandleResize);
    }

    #applyTruncation() {
      if (!this.contentEl || !this.clipEl) return;

      this.#clearClipHeight();
      this.classList.remove('is-collapsed');

      const shouldTruncate = this.#shouldTruncate();

      if (!shouldTruncate) {
        this.#hideToggles();
        return;
      }

      const collapsedHeight = this.#getCollapsedHeight();
      this.#setCollapsedState(collapsedHeight);
      this.#updateToggleState(this.isExpanded);
    }

    #shouldTruncate() {
      const collapsedHeight = this.#getCollapsedHeight();
      return this.contentEl.scrollHeight > collapsedHeight + 1;
    }

    #getCollapsedHeight() {
      const truncateMode = this.dataset.truncateMode;

      if (truncateMode === 'characters') {
        const maxCharacters = Number.parseInt(this.dataset.maxCharacters, 10) || 300;
        return this.#getMaxHeightForCharacters(maxCharacters) || 0;
      }

      const maxLines = Number.parseInt(this.dataset.maxLines, 10) || 4;
      const lineHeight = Number.parseFloat(getComputedStyle(this.contentEl).lineHeight);

      return lineHeight * maxLines;
    }

    #getMaxHeightForCharacters(maxCharacters) {
      const walker = document.createTreeWalker(this.contentEl, NodeFilter.SHOW_TEXT);
      let characterCount = 0;
      let targetNode = null;
      let targetOffset = 0;

      while (walker.nextNode()) {
        const textNode = walker.currentNode;
        const nodeLength = textNode.textContent.length;

        if (characterCount + nodeLength >= maxCharacters) {
          targetNode = textNode;
          targetOffset = maxCharacters - characterCount;
          break;
        }

        characterCount += nodeLength;
      }

      if (!targetNode) return null;

      const range = document.createRange();
      range.setStart(this.contentEl, 0);
      range.setEnd(targetNode, targetOffset);

      const rangeRect = range.getBoundingClientRect();
      const contentRect = this.contentEl.getBoundingClientRect();

      return rangeRect.bottom - contentRect.top;
    }

    #setCollapsedState(collapsedHeight) {
      this.classList.add('is-collapsed');
      this.clipEl.style.maxHeight = `${collapsedHeight}px`;
    }

    #setExpandedState() {
      this.classList.remove('is-collapsed');
      this.#clearClipHeight();
    }

    #clearClipHeight() {
      this.clipEl.style.maxHeight = '';
    }

    #animateHeight(fromHeight, toHeight) {
      this.isAnimating = true;
      this.classList.add('is-animating');
      this.classList.remove('is-collapsed');

      this.clipEl.style.maxHeight = `${fromHeight}px`;
      this.clipEl.offsetHeight;
      this.clipEl.style.maxHeight = `${toHeight}px`;

      return this.#waitForTransition(this.clipEl).finally(() => {
        this.isAnimating = false;
        this.classList.remove('is-animating');
      });
    }

    #waitForTransition(element) {
      return new Promise((resolve) => {
        let isResolved = false;

        const finish = () => {
          if (isResolved) return;

          isResolved = true;
          element.removeEventListener('transitionend', handleTransitionEnd);
          clearTimeout(fallbackTimeoutId);
          resolve();
        };

        const handleTransitionEnd = (event) => {
          if (event.target !== element || event.propertyName !== 'max-height') return;

          finish();
        };

        const fallbackTimeoutId = window.setTimeout(
          finish,
          ByteProductDescription.#ANIMATION_DURATION_MS + 50
        );

        element.addEventListener('transitionend', handleTransitionEnd);
      });
    }

    #prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    #updateToggleState(isExpanded) {
      if (!this.expandButton || !this.collapseButton) return;

      this.expandButton.hidden = isExpanded;
      this.collapseButton.hidden = !isExpanded;
      this.expandButton.setAttribute('aria-expanded', String(isExpanded));
      this.collapseButton.setAttribute('aria-expanded', String(isExpanded));
    }

    #hideToggles() {
      if (!this.expandButton || !this.collapseButton) return;

      this.expandButton.hidden = true;
      this.collapseButton.hidden = true;
    }
  }

  customElements.define('byte-product-description', ByteProductDescription);
}
